import type { RecruitmentStatusResponse } from '@on3/api';
import { On3IconArrow } from '@on3/icons';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { UndecidedStatus } from '@public/components/RecruitmentStatus/UndecidedStatus';
import { ConditionalLink } from '@public/components/UI/StarRating/StarRating';
import { Text } from '@public/components/UI/Typography/Text';
import { format } from 'date-fns';
import { useMemo } from 'react';

import styles from './SlimTransferStatusV1.module.scss';

interface ISlimTransferStatusProps {
  status?: RecruitmentStatusResponse;
}
export const SlimTransferStatusV1 = ({ status }: ISlimTransferStatusProps) => {
  const {
    shortTermSignee,
    committedOrganizationPrimaryColor,
    commitmentDate,
    committedOrganizationAssetUrl,
    committedOrganizationKey,
    committedOrganizationSlug,
    isCommitted,
    isEnrolled,
    isSigned,
    isTransfer,
    recruitmentYear,
    sportName,
    transferredFromOrganizationAssetUrl,
    transferredFromOrganizationSlug,
  } = status ?? {};

  const sport = sportName ? sportName.toLowerCase() : 'football';

  const newTeamColor = committedOrganizationPrimaryColor ?? '#e9ecf3';
  const hasStatus = (isCommitted || isEnrolled || isSigned) && isTransfer;
  const useLastTeamLink =
    !!transferredFromOrganizationSlug && !!recruitmentYear;
  const useNewTeamLink = !!committedOrganizationSlug && !!recruitmentYear;
  const newTeamBorderColor = useMemo(() => {
    return { borderColor: newTeamColor };
  }, [newTeamColor]);

  const commitDate =
    commitmentDate && format(new Date(commitmentDate), 'M/dd/yy');

  return (
    <div className={styles.transferContainer}>
      {shortTermSignee && (
        <Text className={styles.shortTermLabel} variant="subtitle1">
          Short-Term Signee
        </Text>
      )}
      <div className={styles.teamsContainer}>
        <ConditionalLink
          className={styles.lastTeam}
          condition={useLastTeamLink}
          href={`/college/${transferredFromOrganizationSlug}/${sport}/${recruitmentYear}/transfers/`}
        >
          <figure>
            <TeamAvatar
              className={styles.teamLogo}
              height={20}
              slug={transferredFromOrganizationSlug}
              src={transferredFromOrganizationAssetUrl}
              width={20}
            />
          </figure>
        </ConditionalLink>
        <On3IconArrow />

        {hasStatus && (
          <ConditionalLink
            className={styles.newTeam}
            condition={useNewTeamLink}
            href={`/college/${committedOrganizationSlug}/${sport}/${recruitmentYear}/transfers/`}
          >
            <figure>
              <TeamAvatar
                className={styles.teamLogo}
                height={20}
                slug={committedOrganizationSlug}
                src={committedOrganizationAssetUrl}
                width={20}
              />
            </figure>
          </ConditionalLink>
        )}

        {!hasStatus && !committedOrganizationKey && (
          <UndecidedStatus className={styles.undecided} hideText />
        )}
      </div>

      {commitDate && <Text className={styles.commitDate}>{commitDate}</Text>}
    </div>
  );
};
